import React, { useEffect, useState } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

const LOCALLY_INTEGRATED_PATHS = ['/patients', '/audit', '/system-checks', '/patients-bloques'];

export default function GlobalDoctorPresence() {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const [isDoctor, setIsDoctor] = useState(false);
  const [isPresent, setIsPresent] = useState(false);
  const [busy, setBusy] = useState(false);

  const isPublicPage = location.pathname === '/login'
    || location.pathname === '/register'
    || location.pathname === '/doctor-portal'
    || location.pathname.startsWith('/patient/');
  const hasLocalSwitch = LOCALLY_INTEGRATED_PATHS.some((path) => location.pathname === path);

  useEffect(() => {
    if (!token || isPublicPage) {
      setIsDoctor(false);
      return undefined;
    }
    let active = true;
    fetch('/api/doctor-presence/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (!response.ok) throw new Error('presence-load-failed');
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        setIsDoctor(Boolean(data.isDoctor));
        setIsPresent(Boolean(data.isPresent));
      })
      .catch(() => { if (active) setIsDoctor(false); });
    return () => { active = false; };
  }, [token, isPublicPage, location.pathname]);

  const changePresence = async (event) => {
    const nextValue = event.target.checked;
    const previousValue = isPresent;
    setIsPresent(nextValue);
    setBusy(true);
    try {
      const response = await fetch('/api/doctor-presence/me', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPresent: nextValue }),
      });
      if (!response.ok) throw new Error('presence-update-failed');
      const data = await response.json();
      setIsPresent(Boolean(data.isPresent));
    } catch (error) {
      setIsPresent(previousValue);
    } finally {
      setBusy(false);
    }
  };

  if (!isDoctor || isPublicPage || hasLocalSwitch) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.drawer + 3,
        top: { xs: 9, sm: 10 },
        right: { xs: 74, sm: 88 },
        height: 44,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.25, sm: 0.75 },
        px: { xs: 0.6, sm: 1.25 },
        borderRadius: 999,
        color: '#fff',
        border: `1px solid ${isPresent ? 'rgba(134,239,172,.8)' : 'rgba(255,255,255,.46)'}`,
        backgroundColor: isPresent ? 'rgba(22,163,74,.9)' : 'rgba(30,41,59,.82)',
        boxShadow: '0 8px 24px rgba(15,23,42,.24)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: isPresent ? '#86efac' : '#cbd5e1', boxShadow: isPresent ? '0 0 9px #86efac' : 'none' }} />
      <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: 800, whiteSpace: 'nowrap' }}>
        {isPresent ? 'Présent' : 'Absent'}
      </Typography>
      <Switch
        size="small"
        checked={isPresent}
        disabled={busy}
        onChange={changePresence}
        inputProps={{ 'aria-label': 'Statut de présence du médecin' }}
        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#86efac' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4ade80' } }}
      />
    </Box>
  );
}
